import Oidc, {UserManager} from "oidc-client";
import React, {ReactNode, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {CenteredCircularProgress} from "../../components/progress/CenteredCircularProgress";
import {AUTH_REDIRECT_ROUTE, AUTH_SILENT_REDIRECT_ROUTE} from "../../components/router/routes";
import {useConfig} from "../config/ConfigProvider";

export function useUserManager(): UserManager {
  return useContext(UserManagerContext)!!;
}

export const UserManagerContext = React.createContext<UserManager | null>(null);

export function UserManagerProvider(props: {children?: ReactNode}) {
  const config = useConfig();
  const [userManager, setUserManager] = useState<UserManager>();

  const {i18n} = useTranslation();

  useEffect(() => {
    Oidc.Log.logger = console;
    Oidc.Log.level = Oidc.Log.WARN;
    // console.log("Got application config: " + JSON.stringify(authConfig))
    const newUserManger = new UserManager({
      authority: config.oauthUrl,
      client_id: config.oauthClientId,
      response_type: "code",
      redirect_uri: `${window.location.origin}${AUTH_REDIRECT_ROUTE.path}`,
      silent_redirect_uri: `${window.location.origin}${AUTH_SILENT_REDIRECT_ROUTE.path}`,
      userStore: new Oidc.WebStorageStateStore({store: window.localStorage}),
      automaticSilentRenew: true,
    });
    // newUserManger.clearStaleState().then();
    const accessTokenExpiredCallback = () => {
      // console.log("accessToken expired or expiring... sign in silent");
      newUserManger.signinSilent().catch(() => {
        console.info("accessTokenExpiredCallback: singInSilent failed => SigninRedirect");
        newUserManger.signinRedirect({
          extraQueryParams: {
            "kc_locale": i18n.language,
          }
        });
      });
    };
    const silentRenewErrorCallback = () => {
      // console.log("silentRenewError called ");
      newUserManger.signinSilent().catch(() => {
        console.info("silentRenewErrorCallback: singInSilent failed => SigninRedirect");
        newUserManger.signinRedirect({
          extraQueryParams: {
            "kc_locale": i18n.language,
          }
        });
      });
    };
    // console.log("Adding userManager event listeners");
    newUserManger.events.addAccessTokenExpiring(accessTokenExpiredCallback);
    newUserManger.events.addAccessTokenExpired(accessTokenExpiredCallback);
    newUserManger.events.addSilentRenewError(silentRenewErrorCallback);
    // console.log("New user manager created: " + newUserManger)
    setUserManager(newUserManger);
  }, [i18n.language, config]);

  if (!userManager) return <CenteredCircularProgress loadingText={"Loading User Manager"} />;

  // console.log("<UserManagerProvider>: Provided new userManager");
  return (
    <UserManagerContext.Provider value={userManager}>
      {props.children}
    </UserManagerContext.Provider>
  );
}
